import { Component, type ErrorInfo, type ReactNode } from "react";

interface AppErrorBoundaryProps {
  children: ReactNode;
}

interface AppErrorBoundaryState {
  failed: boolean;
}

/** Keeps an unexpected shell failure actionable instead of leaving a blank page. */
export class AppErrorBoundary extends Component<AppErrorBoundaryProps, AppErrorBoundaryState> {
  public override state: AppErrorBoundaryState = { failed: false };

  /** Converts an uncaught render error into the durable fallback. */
  public static getDerivedStateFromError(): AppErrorBoundaryState {
    return { failed: true };
  }

  /** Reports only a generic diagnostic because user content must never enter logs. */
  public override componentDidCatch(_error: Error, _info: ErrorInfo): void {
    console.error("Shotboard encountered an unexpected application error.");
  }

  /** Renders either the application or a recovery-oriented fallback. */
  public override render() {
    if (this.state.failed) {
      return (
        <main className="fatal-error">
          <p className="eyebrow">Shotboard stopped safely</p>
          <h1>The application could not continue.</h1>
          <p>Your workspace files were not changed by this failure.</p>
          <button type="button" onClick={() => window.location.reload()}>
            Reload Shotboard
          </button>
        </main>
      );
    }

    return this.props.children;
  }
}
